import React, { useState, useRef, useEffect, forwardRef, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import cn from 'classnames';
import Image from 'next/image';
import { useId } from '@radix-ui/react-id';

import s from './video.module.css';

const ReactPlayer = dynamic(import('react-player'), {
  ssr: false
});

const Video = forwardRef(
  (
    {
      className,
      poster,
      width,
      height,
      url,
      caption,
      playing,
      fixOnScroll,
      smallButton,
      blackButton = false,
      controls,
      onModalClick,
      muted = true,
      ...rest
    },
    ref
  ) => {
    const id = useId();
    const playerContainerRef = useRef(null);
    const [state, setState] = useState({
      playing,
      fixedVideo: false,
      animate: false
    });

    const handleFixOnScroll = useCallback(() => {
      if (
        window.scrollY >
        playerContainerRef.current.offsetTop + playerContainerRef.current.offsetHeight
      ) {
        if (state.playing && !state.fixedVideo) {
          setState(prev => ({ ...prev, fixedVideo: true }));
        }
      } else if (state.fixedVideo) {
        setState(prev => ({ ...prev, fixedVideo: false }));
      }
    }, [state.playing, state.fixedVideo]);

    const togglePlaying = useCallback(() => {
      if (!state.animate) {
        setState(prev => ({ ...prev, animate: true, playing: true }));
      } else {
        setState(prev => ({ ...prev, playing: !state.playing }));
      }
    }, [state.playing, state.animate]);

    const updateOnEnd = useCallback(() => {
      setState(prev => ({ ...prev, playing: false }));
    }, []);

    useEffect(() => {
      if (fixOnScroll) window.addEventListener('scroll', handleFixOnScroll);

      return () => {
        if (fixOnScroll) window.removeEventListener('scroll', handleFixOnScroll);
      };
    }, [state.imageLoaded, handleFixOnScroll, fixOnScroll]);

    const aspectRatio = (height / width) * 100;

    const config = useMemo(() => {
      const isMux = url?.includes('stream.mux.com');
      const isWistia = url?.includes('wistia.com');

      if (isMux)
        return {
          file: {
            forceHls: true
          }
        };

      if (isWistia)
        return {
          wistia: {
            playerId: id,
            playButton: false,
            playerColor: 'ff0000'
          }
        };

      return undefined;
    }, [url]);

    const videoIsYoutube = url?.startsWith('https://www.youtube.com');

    let videoId;

    if (videoIsYoutube) {
      [, videoId] = url.split('v=');
      if (videoId) {
        const ampersandPosition = videoId.indexOf('&');
        if (ampersandPosition !== -1) {
          videoId = videoId.substring(0, ampersandPosition);
        }
      }
    }

    const isYoutubePoster = !poster && videoIsYoutube && videoId;

    return (
      <div className={className}>
        <div
          className={cn(s.wrapper, {
            [s.fixed]: state.fixedVideo
          })}
          style={{ paddingBottom: `${aspectRatio}%` }}
          ref={playerContainerRef}
        >
          {onModalClick && (
            <div
              onClick={onModalClick}
              className="absolute inset-0 z-10 hidden cursor-pointer lg:block"
            />
          )}
          <div className={cn(s['video-container'])}>
            <div className={s['video-position']} style={{ paddingBottom: `${aspectRatio}%` }}>
              <ReactPlayer
                className="absolute inset-0"
                height="100%"
                width="100%"
                {...rest}
                controls={controls}
                playing={playing || state.playing}
                muted={muted}
                url={url}
                onEnded={updateOnEnd}
                config={config}
                ref={ref}
              />
            </div>
          </div>

          <div
            className={cn('group absolute -inset-4', !playing && s['play-trap'], {
              [s.animate]: state.animate
            })}
            onClick={togglePlaying}
          >
            {(poster || isYoutubePoster) && (
              <>
                <div className={cn(s['photo-wrapper'], { [s.animate]: state.animate })}>
                  <Image
                    src={poster ?? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                    objectFit="cover"
                    layout="fill"
                    loading="eager"
                    quality={100}
                    priority
                  />
                </div>
                <div className={cn(s['play-button'], blackButton && s['black-button'])}>
                  {smallButton ? (
                    <span className="w-[80px] h-[80px]">
                      <svg
                        width={36}
                        height={36}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 60 60"
                      >
                        <path
                          fill="#FFF"
                          d="M48.79 26.129l-33-19.508C13.104 5.035 9 6.574 9 10.492V49.5c0 3.516 3.816 5.637 6.79 3.871l33-19.5c2.94-1.734 2.952-6.008 0-7.742zm0 0"
                        />
                      </svg>
                    </span>
                  ) : (
                    <span className="w-[124px] h-[124px]">
                      <svg
                        width={60}
                        height={60}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 60 60"
                      >
                        <path
                          fill="#FFF"
                          d="M48.79 26.129l-33-19.508C13.104 5.035 9 6.574 9 10.492V49.5c0 3.516 3.816 5.637 6.79 3.871l33-19.5c2.94-1.734 2.952-6.008 0-7.742zm0 0"
                        />
                      </svg>
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {caption && (
          <div className="mt-5 text-silver text-base leading-6 tracking-[-0.01em]">{caption}</div>
        )}
      </div>
    );
  }
);

Video.defaultProps = {
  width: 1920,
  height: 1080,
  fixOnScroll: false,
  poster: undefined,
  url: undefined,
  caption: undefined,
  smallButton: false,
  controls: true,
  onModalClick: false
};

export default Video;
